import React, { useState } from "react";
import { Card } from "primereact/card";
import {
  useStartTelegramMutation,
  useVerifyTelegramMutation,
  useResendTelegramMutation,
} from "../../redux/services/telegram";
import { useGetAuthUrlMutation } from "../../redux/services/google";
import {
  useSendPhoneOtpMutation,
  useVerifyPhoneMutation,
  useResendPhoneMutation,
} from "../../redux/services/phone";
import getSubdomain from "../../utils/getSubdomain";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/slices/user.slice";
import { useNavigate } from "react-router-dom";
import useOtpTimer from "../../hooks/useOtpTimer";
import useEmailAuth from "./components/StudentLoginSteps/useEmailAuth";
import SendCodeStep from "./components/StudentLoginSteps/SendCodeStep";
import OtpCodeStep from "./components/StudentLoginSteps/OtpCodeStep";
import * as styles from "./StudentLogin.module.css";

const BOT_NICKNAME = process.env.TELEGRAM_BOT_NICKNAME || "DwtlLoginBot";

const MAX_OTP_RETRIES = 3;
const RETRY_OTP_MESSAGE = "Invalid code. Please try again.";

const OTP_ERROR_MESSAGES = {
  invalid_otp: "Invalid or expired code. Request a new one.",
  expired: "Code expired. Request a new one.",
  already_used: "Code already used. Request a new one.",
  too_many_attempts: "Too many attempts. Request a new one.",
  locked: "Too many attempts. Please wait or request a new one.",
};

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const METHODS = [
  { id: "email", icon: <i className="pi pi-envelope" />, label: "Email" },
  { id: "phone", icon: <i className="pi pi-phone" />, label: "Phone" },
  { id: "telegram", icon: <i className="pi pi-send" />, label: "Telegram" },
  // { id: "google", icon: <GoogleIcon />, label: "Google" },
];

const StudentLogin = () => {
  const [activeMethod, setActiveMethod] = useState("email");
  const [step, setStep] = useState("input");
  const [inputValue, setInputValue] = useState("");
  const [otp, setOtp] = useState("");
  const [startToken, setStartToken] = useState(null);
  const [mustResend, setMustResend] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { remaining, reset: resetTimer } = useOtpTimer(step === "otp");

  const [sendPhoneOtp, { isLoading: sendingPhone }] = useSendPhoneOtpMutation();
  const [verifyPhone, { isLoading: verifyingPhone }] = useVerifyPhoneMutation();
  const [resendPhone, { isLoading: resendingPhone }] = useResendPhoneMutation();
  const [startTelegram] = useStartTelegramMutation();
  const [verifyTelegram, { isLoading: verifyingTelegram }] =
    useVerifyTelegramMutation();
  const [resendTelegram, { isLoading: resendingTelegram }] =
    useResendTelegramMutation();
  const [getAuthUrl] = useGetAuthUrlMutation();

  const handleSuccess = (data) => {
    try {
      if (!data) return;
      const profile = data.profile || data;
      const access =
        data.access_token ||
        data.token ||
        (typeof data === "string" ? data : null);
      const refresh = data.refresh_token || null;
      if (access) localStorage.setItem("access_token", access);
      if (refresh) localStorage.setItem("refresh_token", refresh);
      if (profile) dispatch(setUser(profile));
    } catch (e) {
      console.error("handleSuccess error:", e);
    }
    navigate("/");
  };

  const startOtpStep = (token) => {
    setStartToken(token);
    setStep("otp");
    setOtp("");
    setMustResend(false);
    setError(null);
    resetTimer();
  };

  const handleOtpError = (msg, attempts = 0) => {
    if (msg === "invalid_otp" && attempts < MAX_OTP_RETRIES) {
      setError(RETRY_OTP_MESSAGE);
      return;
    }
    if (OTP_ERROR_MESSAGES[msg]) {
      setError(OTP_ERROR_MESSAGES[msg]);
      setMustResend(true);
      return;
    }
    setError(msg);
  };

  const emailAuth = useEmailAuth({
    onStarted: startOtpStep,
    onSuccess: handleSuccess,
    onError: handleOtpError,
  });

  const resetPanel = () => {
    setStep("input");
    setInputValue("");
    setOtp("");
    setStartToken(null);
    setMustResend(false);
    setError(null);
  };

  const openGoogle = async () => {
    try {
      const data = await getAuthUrl({ subdomain: getSubdomain() }).unwrap();
      const url = data?.auth_url || data?.authUrl || data?.url || null;
      if (url) window.location.href = url;
      else
        console.error("No auth_url returned from server for Google auth", data);
    } catch (e) {
      console.error("Failed to get Google auth url", e);
    }
  };

  const handleCircleClick = async (id) => {
    if (id === "google") {
      openGoogle();
      return;
    }
    if (activeMethod === id) {
      setActiveMethod(null);
      resetPanel();
      return;
    }
    setActiveMethod(id);
    resetPanel();
    if (id === "telegram") {
      try {
        const data = await startTelegram().unwrap();
        const tokenId =
          data?.start_token || data?.startToken || data?.id || null;
        const link = tokenId
          ? `https://t.me/${BOT_NICKNAME}?start=${encodeURIComponent(tokenId)}`
          : `https://t.me/${BOT_NICKNAME}`;
        window.open(link, "_blank", "noopener,noreferrer");
        setStartToken(tokenId);
      } catch (e) {
        console.error(e);
        window.open(
          `https://t.me/${BOT_NICKNAME}`,
          "_blank",
          "noopener,noreferrer",
        );
      }
      setStep("otp");
    }
  };

  const handlePhoneSendOtp = async () => {
    setError(null);
    try {
      const data = await sendPhoneOtp({ phone: inputValue }).unwrap();
      startOtpStep(data.start_token || data.startToken || null);
    } catch (e) {
      setError(e.data?.message || e.message || "Failed to send code");
    }
  };

  const handlePhoneVerify = async () => {
    setError(null);
    try {
      const data = await verifyPhone({ start_token: startToken, otp }).unwrap();
      handleSuccess(data);
    } catch (e) {
      handleOtpError(
        e.data?.message || e.message || "Verification failed",
        e.data?.attempts,
      );
    }
  };

  const handlePhoneResend = async () => {
    setError(null);
    try {
      const data = await resendPhone({ start_token: startToken }).unwrap();
      startOtpStep(data.start_token || data.startToken || startToken);
    } catch (e) {
      setError(e.data?.message || e.message || "Resend failed");
    }
  };

  const handleTelegramVerify = async () => {
    setError(null);
    try {
      const data = await verifyTelegram({
        start_token: startToken,
        otp,
      }).unwrap();
      handleSuccess(data);
    } catch (e) {
      handleOtpError(
        e.data?.message || e.message || "Verification failed",
        e.data?.attempts,
      );
    }
  };

  const handleTelegramResend = async () => {
    setError(null);
    try {
      const data = await resendTelegram({ start_token: startToken }).unwrap();
      const newToken = data.start_token || data.startToken || startToken;
      window.open(
        `https://t.me/${BOT_NICKNAME}?start=${encodeURIComponent(newToken)}`,
        "_blank",
        "noopener,noreferrer",
      );
      startOtpStep(newToken);
    } catch (e) {
      setError(e.data?.message || e.message || "Resend failed");
    }
  };

  const isLoading =
    emailAuth.sending ||
    emailAuth.verifying ||
    emailAuth.resending ||
    sendingPhone ||
    verifyingPhone ||
    resendingPhone ||
    verifyingTelegram ||
    resendingTelegram;

  const otpHint = (
    <>
      Code sent to <strong>{inputValue}</strong>
    </>
  );

  const renderPanel = () => {
    if (!activeMethod || activeMethod === "google") return null;

    if (activeMethod === "email") {
      if (step === "otp")
        return (
          <OtpCodeStep
            hint={otpHint}
            otp={otp}
            onOtpChange={setOtp}
            onVerify={() => emailAuth.verify(startToken, otp)}
            onResend={() => emailAuth.resend(startToken)}
            verifying={emailAuth.verifying}
            busy={isLoading}
            remaining={remaining}
            mustResend={mustResend}
            error={error}
          />
        );
      return (
        <SendCodeStep
          type="email"
          value={inputValue}
          onChange={setInputValue}
          placeholder="your@email.com"
          autoComplete="off"
          onSubmit={() => emailAuth.sendOtp(inputValue)}
          submitting={emailAuth.sending}
          busy={isLoading}
          submitLabel="Send Code"
          error={error}
        />
      );
    }

    if (activeMethod === "phone") {
      if (step === "otp")
        return (
          <OtpCodeStep
            hint={otpHint}
            otp={otp}
            onOtpChange={setOtp}
            onVerify={handlePhoneVerify}
            onResend={handlePhoneResend}
            verifying={verifyingPhone}
            busy={isLoading}
            remaining={remaining}
            mustResend={mustResend}
            error={error}
          />
        );
      return (
        <SendCodeStep
          type="tel"
          value={inputValue}
          onChange={setInputValue}
          placeholder="+1234567890"
          autoComplete="tel"
          onSubmit={handlePhoneSendOtp}
          submitting={sendingPhone}
          busy={isLoading}
          submitLabel="Send Code"
          error={error}
        />
      );
    }

    if (activeMethod === "telegram")
      return (
        <OtpCodeStep
          hint="Enter the code you received in Telegram."
          otp={otp}
          onOtpChange={setOtp}
          onVerify={handleTelegramVerify}
          onResend={handleTelegramResend}
          verifying={verifyingTelegram}
          busy={isLoading}
          remaining={remaining}
          mustResend={mustResend}
          error={error}
        />
      );
  };

  return (
    <div className={styles.wrapper}>
      <Card title="Let's verify you" className={styles.card}>
        <p>
          Pick how you&apos;d like to receive a code <br /> so we can{" "}
          <strong style={{ color: "#383838" }}>
            confirm it&apos;s really you.
          </strong>
        </p>
        <div className={styles.methodRow}>
          {METHODS.map(({ id, icon, label }) => (
            <div key={id} className={styles.methodItem}>
              <button
                type="button"
                className={`${styles.methodCircle} ${id === "google" ? styles.methodCircleGoogle : ""} ${activeMethod === id ? styles.methodCircleActive : ""}`}
                onClick={() => handleCircleClick(id)}
                aria-label={`Authenticate via ${label}`}
              >
                {icon}
              </button>
              <span className={styles.methodLabel}>{label}</span>
            </div>
          ))}
        </div>
        {renderPanel()}
      </Card>
    </div>
  );
};

export default StudentLogin;
